import { useMutation, useQuery } from "@tanstack/react-query";
import { instance } from "api/apiBase";
import { useState } from "react";
import { toast } from "react-toastify";
import { Button, ButtonVariant, Modal, Show } from "shared/components";
import { Applicant } from "types/application";
import { getErrorMessage } from "utils/error";
import { getPresignedUrl, uploadFileToS3Bucket } from "utils/fileHandlers";

import { IntroView } from "./slices/IntroView";
import { getApplicantRole } from "utils/getApplicantRole";
import { getFullName } from "shared/utils/get-full-name";
import { CombinedIncome } from "../../types/income";

import { DocType } from "./types";
import { DocTypeView } from "./slices/DocTypeView";
import { DropZoneView } from "./slices/DropZoneView";
import { ConfirmView } from "./slices/ConfirmView";
import { getIncomeJobName } from "../../utils";
import { normalizeFilename } from "shared/utils";

enum Views {
  Intro,
  DocType,
  UploadDoc,
  Confirm
}

const JOURNEY = [Views.Intro, Views.DocType, Views.UploadDoc, Views.Confirm];
export const ReverifyIncomeModal = ({
  opened,
  onClose,
  applicant,
  applicationId,
  verificationId,
  incomeId,
  income,
  onSuccess
}: {
  opened: boolean;
  onClose: () => void;
  applicationId: number | string;
  applicant: Applicant;
  verificationId: number | string;
  incomeId: string | number;
  income?: CombinedIncome;
  onSuccess: () => void;
}) => {
  const [view, setView] = useState<Views>(Views.Intro);
  const [docType, setDocType] = useState<DocType>(DocType.Paystub);
  const [files, setFiles] = useState<File[]>([]);

  const { data: reVerificationPrice, isLoading } = useQuery<number>({
    queryKey: ["REVERIFICATION_PRICE"],
    queryFn: () =>
      instance
        .get("portal/v1/partners/combined_income_reverification_price")
        .then(({ data }) => data.combined_income_reverification_price_cents),
    enabled: view === Views.Confirm
  });

  const mutation = useMutation({
    mutationKey: [
      "REVERIFY",
      "APPLICATION",
      String(applicationId),
      "VERIFICATION",
      String(verificationId),
      "APPLICANT",
      String(applicant.id)
    ],
    mutationFn: ({ docType, files }: { docType: DocType; files: File[] }) => {
      return Promise.all(
        files.map((file) => {
          return getPresignedUrl(file)
            .then((url) => uploadFileToS3Bucket(url, file))
            .then(({ url }) => url);
        })
      ).then((links) => {
        const ret = {
          income_source_type: docType,
          documents: files.map((file, idx) => ({
            document_type: docType,
            url: links[idx],
            metadata: {
              size: file.size,
              original_filename: normalizeFilename(file.name),
              content_type: file.type
            }
          }))
        };

        return instance.post(
          `portal/v1/applications/${applicationId}/verifications/${verificationId}/income/${incomeId}/reverify?applicant_id=${applicant.id}`,
          ret
        );
      });
    },
    onError: (err) => {
      toast.error(getErrorMessage(err, "Error reverifying income"));
    },
    onSuccess: () => {
      onSuccess();
    }
  });

  if (!opened || !income) return null;

  const applicantFullName = getFullName({
    firstName: applicant.first_name,
    middleName: applicant.middle_name,
    lastName: applicant.last_name,
    short: true
  });

  return (
    <Modal
      opened
      onClose={onClose}
      panelClassName="max-w-[488px] w-full h-[640px] flex flex-col"
    >
      <div className="flex-grow  min-h-0">
        <Show if={view === Views.Intro}>
          <IntroView
            onClose={onClose}
            applicantRole={getApplicantRole(applicant.role)}
            applicantFullName={applicantFullName}
            incomeStartDate={income.start_date || "-"}
            incomeEndDate="Present"
            incomeSource={income.type}
            incomeName={getIncomeJobName(income)}
          />
        </Show>

        <Show if={view === Views.DocType}>
          <DocTypeView
            onClose={onClose}
            docType={docType}
            setDocType={setDocType}
            applicantFullName={applicantFullName}
          />
        </Show>

        <Show if={view === Views.UploadDoc}>
          <DropZoneView
            onClose={onClose}
            companyName={
              income.additional_data && "company_name" in income.additional_data
                ? income.additional_data.company_name
                : undefined
            }
            applicantFullName={applicantFullName}
            files={files}
            setFiles={setFiles}
            docType={docType}
          />
        </Show>

        <Show if={view === Views.Confirm}>
          <ConfirmView
            onClose={onClose}
            applicantRole={getApplicantRole(applicant.role)}
            applicantFullName={applicantFullName}
            cents={reVerificationPrice || 0}
            isLoading={isLoading}
            incomeSource={income.type}
            incomeName={getIncomeJobName(income)}
            documentsCount={files.length}
            docType={docType}
          />
        </Show>
      </div>

      <div className="px-6 py-4 border-t border-alpha-100 gap-2 flex justify-end self-end w-full">
        <Button
          className="w-[125px]"
          onClick={() => {
            if (view === Views.Intro) onClose();
            else setView(JOURNEY[JOURNEY.indexOf(view) - 1]);
          }}
        >
          {view === Views.Intro ? "Cancel" : "Back"}
        </Button>

        <Button
          variant={ButtonVariant.PRIMARY}
          className="w-[125px]"
          disabled={view === Views.UploadDoc && !files.length}
          loading={mutation.isPending || (isLoading && view === Views.Confirm)}
          onClick={async () => {
            if (view === Views.Confirm)
              mutation.mutate({
                docType,
                files
              });
            else setView(JOURNEY[JOURNEY.indexOf(view) + 1]);
          }}
        >
          {view === Views.Confirm ? "Verify" : "Continue"}
        </Button>
      </div>
    </Modal>
  );
};
